'use client';

import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaCheck, FaTimes, FaAndroid } from 'react-icons/fa';
import { captureEmail } from '@/utils/captureEmail';
import { track } from '@/utils/analytics';
import Confetti from './Confetti';

interface WaitlistModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Stable source label so we can break down waitlist conversion by surface
   *  (home_hero, home_cta_section, scanner_page, blog_inline, etc). */
  source?: string;
}

export default function WaitlistModal({ isOpen, onClose, source = 'unknown' }: WaitlistModalProps) {
  const nameInputRef = useRef<HTMLInputElement>(null);

  // Fire `android_waitlist_open` once per open transition. Useful funnel
  // signal — the open/submit ratio tells us whether the form itself is the
  // drop-off, vs. the CTA copy that triggers it.
  useEffect(() => {
    if (isOpen) {
      track('android_waitlist_open', { source });
      // Move focus into the form on open (accessibility + faster completion).
      const t = setTimeout(() => nameInputRef.current?.focus(), 60);
      return () => clearTimeout(t);
    }
  }, [isOpen, source]);

  // Esc-to-close — standard dialog affordance.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Reset transient state whenever the modal is (re)opened, so a returning
  // user never sees a stale success/error panel from a previous session.
  useEffect(() => {
    if (isOpen) {
      // This one IS the reset-on-prop-change pattern the rule targets. The
      // idiomatic fix is a `key` on the modal at each call site, which is a
      // parent-side change and out of scope for a framework upgrade. Left as-is
      // deliberately; revisit when touching the modal's callers.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSubmitStatus('idle');
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    const emailParams = {
      from_name: formData.name,
      reply_to: formData.email,
      subject: '',
      message: '',
      type: 'android_waitlist' as const
    };

    try {
      const result = await captureEmail({
        email: formData.email,
        list: 'android_waitlist',
        source: 'android_waitlist_modal',
        context: source,
        emailParams,
      });

      if (result.success) {
        setSubmitStatus('success');
        // KEY EVENT — this is one of the primary conversions the site is
        // measured on. `source` lets us see which surface (hero, footer,
        // landing page) actually converts.
        track('android_waitlist_submit', { source });
        setFormData({
          name: '',
          email: '',
        });
        // Let the celebration play, then close.
        setTimeout(() => {
          onClose();
        }, 3200);
      } else {
        setSubmitStatus('error');
        track('android_waitlist_error', { source });
      }
    } catch {
      setSubmitStatus('error');
      track('android_waitlist_error', { source });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const isSuccess = submitStatus === 'success';

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="waitlist-modal-title"
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-charcoal/60 backdrop-blur-sm"
            onClick={onClose}
            aria-hidden="true"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 24 }}
            transition={{ type: 'spring', stiffness: 320, damping: 26 }}
            className="relative overflow-hidden bg-white rounded-t-2xl sm:rounded-3xl shadow-2xl shadow-charcoal/25 w-full max-w-md max-h-[90dvh] overflow-y-auto pb-[max(1.5rem,env(safe-area-inset-bottom))] ring-1 ring-black/5"
          >
            {/* Top accent bar — subtle Android-green → brand-orange gradient so
                the modal reads as "flashier" without cartoonish noise. */}
            <div
              aria-hidden="true"
              className="h-1.5 w-full bg-gradient-to-r from-[#3DDC84] via-[#1E6B47] to-[#155235]"
            />

            <button
              onClick={onClose}
              className="absolute top-3 right-3 z-10 tap-target inline-flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-800 hover:bg-gray-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1E6B47]"
              aria-label="Close waitlist dialog"
            >
              <FaTimes className="w-5 h-5" aria-hidden="true" />
            </button>

            <div className="p-6 md:p-8">
              {isSuccess ? (
                // ── Celebration success state ────────────────────────────────
                <div className="relative py-6 text-center" role="status" aria-live="polite">
                  <Confetti />
                  <motion.div
                    initial={{ scale: 0, rotate: -25 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ type: 'spring', stiffness: 260, damping: 15 }}
                    className="relative mx-auto mb-5 inline-flex h-16 w-16 items-center justify-center rounded-full bg-[#3DDC84]/15 ring-8 ring-[#3DDC84]/10"
                  >
                    <motion.span
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ delay: 0.16, type: 'spring', stiffness: 320, damping: 14 }}
                    >
                      <FaCheck className="h-7 w-7 text-[#25a35c]" aria-hidden="true" />
                    </motion.span>
                  </motion.div>
                  <motion.h2
                    id="waitlist-modal-title"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.12 }}
                    className="text-2xl font-bold text-gray-900"
                  >
                    You&apos;re on the list!
                  </motion.h2>
                  <motion.p
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.22 }}
                    className="mx-auto mt-2 max-w-xs text-gray-600 leading-relaxed"
                  >
                    We&apos;ll email you the day the Android app lands in the Play Store.
                    One email, no spam. Promise.
                  </motion.p>
                  <motion.button
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.35 }}
                    onClick={onClose}
                    className="mt-6 inline-flex items-center justify-center rounded-control bg-charcoal px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-charcoal/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1E6B47] focus-visible:ring-offset-2 pressable"
                  >
                    Done
                  </motion.button>
                </div>
              ) : (
                // ── Form state ───────────────────────────────────────────────
                <>
                  <span className="mb-3 inline-flex items-center gap-2 rounded-full bg-[#3DDC84]/12 px-3 py-1 text-2xs font-semibold uppercase tracking-[0.16em] text-[#1f9553]">
                    <FaAndroid className="w-3.5 h-3.5" aria-hidden="true" />
                    Android · Early access
                  </span>
                  <h2
                    id="waitlist-modal-title"
                    className="font-display text-2xl font-bold mb-2 text-ink pr-10"
                  >
                    Be first on Android
                  </h2>
                  <p className="text-gray-600 mb-6 text-base leading-relaxed">
                    Join the waitlist and we&apos;ll send you a single launch-day
                    email the moment the Android app goes live. No spam, ever.
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label htmlFor="waitlist-name" className="block text-sm font-medium text-gray-700 mb-1">
                        Your Name
                      </label>
                      <input
                        ref={nameInputRef}
                        type="text"
                        id="waitlist-name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        autoComplete="name"
                        className="w-full px-4 py-3 text-base border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1E6B47] focus:border-transparent transition-[color,background-color,border-color,text-decoration-color] duration-200"
                        placeholder="Enter your name"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="waitlist-email" className="block text-sm font-medium text-gray-700 mb-1">
                        Email Address
                      </label>
                      <input
                        type="email"
                        id="waitlist-email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        autoComplete="email"
                        inputMode="email"
                        className="w-full px-4 py-3 text-base border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1E6B47] focus:border-transparent transition-[color,background-color,border-color,text-decoration-color] duration-200"
                        placeholder="Enter your email"
                        required
                      />
                    </div>
                    <motion.button
                      type="submit"
                      disabled={isSubmitting}
                      whileHover={{ y: isSubmitting ? 0 : -2 }}
                      whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                      className={`w-full bg-gradient-to-r from-[#1E6B47] to-[#155235] text-white min-h-[48px] py-3 px-6 rounded-xl shadow-lg shadow-[#1E6B47]/30 hover:shadow-xl hover:shadow-[#1E6B47]/40 transition-shadow duration-200 flex items-center justify-center gap-2 font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1E6B47] focus-visible:ring-offset-2 ${
                        isSubmitting ? 'opacity-80 cursor-not-allowed' : ''
                      }`}
                    >
                      {isSubmitting ? (
                        <>
                          <span className="h-4 w-4 rounded-full border-2 border-white/40 border-t-white animate-spin" aria-hidden="true" />
                          <span>Joining…</span>
                        </>
                      ) : (
                        <span>Join the Waitlist</span>
                      )}
                    </motion.button>
                    {submitStatus === 'error' && (
                      <p className="text-red-500 text-sm text-center" role="alert">
                        Failed to join waitlist. Please try again in a moment.
                      </p>
                    )}
                    <p className="text-center text-xs text-gray-400">
                      One email at launch · No spam · Unsubscribe anytime
                    </p>
                  </form>
                </>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
